import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import history from '../../../../history';
import TierDescription from '../tierdescription/TierDescription';
import TierDescriptionB from '../tierdescription/TierDescriptionB';
import DiscountedPriceNotification from '../discountedPriceNotification/DiscountedPriceNotification.loadable';
import Button, {
	BUTTON_KIND_TRANSPARENT,
	BUTTON_KIND_BRAND,
} from '../../../common/buttons/button/Button';
import {
	SUBSCRIPTION_TIER_B_TITLE,
	CANCEL,
	CONFIRM,
	NOTIFICATION_CHANGE_TIERS_DISCOUNTED_PRICE,
	NOTIFICATION_CHANGE_TIERS_PROMO,
	DOWNGROADE_CHANGE_TIER_CONFIRM_BUTTON,
	CHANGE_TIER_ACTION_BUTTON,
	BACK,
	SUBSCRIPTION_TIER_B_CHANGE_INFO,
	BLACK_FRIDAY,
	ONBOARDING_TIER_OPTIONS_SUBTITLE_PROMO_BENEFIT_B,
	CHANGE_TIER_FOOTNOTE,
	PRICE_INCREASE,
	PRICE_INCREASE_HELP_LINK,
} from '../../../../constants/Copy';
import { ROUTE_ACCOUNT_BEDROCK, ROUTE_ACTIVATE_PLAN } from '../../../../constants/Routes';
import {
	EVT_CHANGE_TIER_CANCEL,
	EVT_CHANGE_TIER_SUBMIT,
	EVT_CHANGE_TIER_NOTIFICATION_DISCOUNTED_PRICE,
} from '../../../../constants/EventTypes';
import './ChangeTier.scss';
import { getTierInfo, isOnFreeTier } from '../../../../helpers/tiers';
import { showNotification } from '../../../../actions/notificationActions';
import { useDispatch } from 'react-redux';
import {
	CHANGE_TIER_NOTIFICATION,
	CHANGE_TIER_QUEUED_NOTIFICATION,
} from '../../../../constants/Notifications';
import { TIER_FREE, TIER_RATEPLAN_SLUGS, TIER_IDS } from '../../../../constants/Tiers';
import { generatePath } from 'react-router';
import useSearchParams from '../../../../hooks/useSearchParams';
import { PARAM_NAMES } from '../../../../constants/Checkout';
import { useVideolandAccountState } from '../../../../hooks/useVideolandAccountState';
import { TIER_SWITCH_TYPES } from '../../../../constants/TierSwitch';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import {
	EVT_ACCOUNT,
	EVT_ELEMENT_INTERACTION,
	EVT_SUBSCRIPTION_CHANGE,
	EVT_CONFIRM,
	EVT_ELEMENT_TYPE_BUTTON,
	EVT_SUBSCRIPTION_CHANGE_CONFIRM,
	EVT_SELECT,
	EVT_SUBSCRIPTION_CHANGE_PROCEED,
} from '../../../../constants/SnowplowEvents';
import { TierTitles } from '../../../../constants/TierTitles';
import { formatPrice } from '../../../../helpers/number';
import TierList from '../tierdescription/TierList';
import { TierPresentationsC } from '../../../../constants/TierPresentations';
import ChangeTierModal from './ChangeTierModal';
import Error from '../../../common/error/Error';
import Spinner from '../../../common/spinner/Spinner';
import { getImageUrl } from '../../../../config';
import { getTrialPeriodText } from '../../../../helpers/sitespect';
import Icon from '../../../common/icons/Icon';
import { getExperiment } from '../../../../helpers/sitespect';
import { SITESPECT_EXPERIMENT_NAMES } from '../../../../constants/Sitespect';
import { ACTIVE_PLAN_PERIOD } from '../../../../constants/Plans';

const scrollToTier = (tierId) => {
	const tierElement = document.querySelector(`.${tierId}`);

	if (tierElement) {
		tierElement.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'center',
		});
	} else {
		// Fallback: try again after a short delay if element not found
		setTimeout(() => {
			const retryElement = document.querySelector(`.${tierId}`);
			retryElement?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}, 50);
	}
};

const ChangeTier = (props) => {
	const {
		currentTier,
		tierCollection,
		isTiersPending,
		isTiersError,
		changeSubscriptionTier,
		analyticsPush,
	} = props;

	const [selectedTier, setSelectedTier] = useState('');
	const [showChangeTierModal, setShowChangeTierModal] = useState(false);
	const tierCollectionFull = useMemo(() => [...tierCollection, TIER_FREE], [tierCollection]);
	const hasInitialScrolled = useRef(false);
	const activeSubscription = useSelector((state) => state.subscription.details?.data);
	const isTrustedParty = !!activeSubscription?.trustedParty;
	const subscriptionNumber = activeSubscription?.subscriptionNumber;
	const currentTierId = currentTier.currentPlan.planDescription.tierId;
	const isUserOnFreeTier = isOnFreeTier(currentTierId);
	const isUserOnPromoPeriod = currentTier.currentPlan.activePlanPeriod === ACTIVE_PLAN_PERIOD.PROMO;
	const displayedTierCollection = useMemo(() => {
		return isUserOnFreeTier ? tierCollectionFull : tierCollection;
	}, [isUserOnFreeTier, tierCollectionFull, tierCollection]);

	const { getSearchParam } = useSearchParams();
	const redirect = getSearchParam(PARAM_NAMES.REDIRECT_URL);
	const gigyaUser = useSelector((state) => state.authentication.gigyaUser);
	const tierPresentation = useMemo(() => TierPresentationsC[selectedTier], [selectedTier]);
	const {
		isBlackFriday: isBlackFridayPeriod,
		showPromo,
		priceIncrease,
	} = useVideolandAccountState();
	const blackFridayIsActive = isBlackFridayPeriod && isUserOnFreeTier;
	const isBiggerTierCardsExperiment =
		getExperiment(SITESPECT_EXPERIMENT_NAMES.BIGGER_TIER_CARDS) === 'variant';

	// Set CSS custom property for dynamic grid columns
	useEffect(() => {
		const tierCount = displayedTierCollection.length;
		document.documentElement.style.setProperty('--tier-columns', tierCount.toString());

		// Cleanup on unmount
		return () => {
			document.documentElement.style.removeProperty('--tier-columns');
		};
	}, [displayedTierCollection.length]);

	useEffect(() => {
		const isTierSwitchBlocked =
			isTrustedParty ||
			currentTier?.tierSwitch?.__typename !== TIER_SWITCH_TYPES.ALLOWED_TIER_SWITCH;
		if (isTierSwitchBlocked) {
			history.replace(generatePath(ROUTE_ACCOUNT_BEDROCK));
		}

		setSelectedTier(currentTierId);
	}, [currentTierId, isTrustedParty, currentTier]);

	// Auto-scroll to current tier only on initial mount
	useEffect(() => {
		if (currentTierId && selectedTier && !hasInitialScrolled.current) {
			hasInitialScrolled.current = true;
			requestAnimationFrame(() => {
				scrollToTier(currentTierId);
			});
		}
	}, [currentTierId, selectedTier]);

	const isSelected = useCallback((tier) => selectedTier === tier.id, [selectedTier]);
	const isCurrentTier = useCallback((tier) => currentTierId === tier.id, [currentTierId]);
	const dispatch = useDispatch();
	const confirmButtonDisabled = currentTierId === selectedTier;

	const onTierChange = useCallback((tierId) => {
		scrollToTier(tierId);
		setSelectedTier(tierId);
	}, []);

	const navigationState = useMemo(() => {
		const currentTierIndex = displayedTierCollection.findIndex((tier) => tier.id === selectedTier);
		return {
			currentTierIndex,
			isFirstTier: currentTierIndex === 0,
			isLastTier: currentTierIndex === displayedTierCollection.length - 1,
		};
	}, [displayedTierCollection, selectedTier]);

	const handleCarouselNavigation = useCallback(
		(direction) => {
			const { currentTierIndex } = navigationState;
			let newIndex;

			if (direction === 'left') {
				newIndex = currentTierIndex > 0 ? currentTierIndex - 1 : displayedTierCollection.length - 1; // Wrap to last
			} else {
				newIndex = currentTierIndex < displayedTierCollection.length - 1 ? currentTierIndex + 1 : 0; // Wrap to first
			}

			const targetTier = displayedTierCollection[newIndex];
			onTierChange(targetTier.id);
		},
		[navigationState, displayedTierCollection, onTierChange]
	);

	const changeTier = useCallback(() => {
		const { pos, price, title } = getTierInfo(selectedTier, tierCollection);

		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CHANGE,
			label: EVT_CONFIRM,
			rtlUser: { rtlUserId: gigyaUser.UID },

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION_CHANGE,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: EVT_SUBSCRIPTION_CHANGE_CONFIRM,
				elementLabel: DOWNGROADE_CHANGE_TIER_CONFIRM_BUTTON,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CHANGE,
				funnelStepName: 'Confirm Subscription Change',
				funnelStep: '03',
				funnelSubscriptionType: `${currentTierId}|${selectedTier}`,
			},
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});

		if (isUserOnFreeTier) {
			const plan = showPromo
				? TIER_RATEPLAN_SLUGS[selectedTier].promo
				: TIER_RATEPLAN_SLUGS[selectedTier].evergreen;
			const upgradeUrl = `${generatePath(ROUTE_ACTIVATE_PLAN, {
				plan,
			})}?${PARAM_NAMES.UPGRADE}=tv-gemist`;
			history.replace(upgradeUrl);
		} else {
			analyticsPush({ productName: title, label: CONFIRM, pos, price }, EVT_CHANGE_TIER_SUBMIT);
			changeSubscriptionTier(selectedTier).then((switchTier) => {
				const effectiveDate = new Date(switchTier.effectiveDate);
				const today = new Date();

				if (effectiveDate <= today) {
					dispatch(showNotification(CHANGE_TIER_NOTIFICATION));
					if (
						currentTierId === TIER_IDS.ESSENTIAL &&
						(selectedTier === TIER_IDS.STANDARD || selectedTier === TIER_IDS.PREMIUM)
					) {
						history.push(
							`${ROUTE_ACCOUNT_BEDROCK}?switchedTierFrom=${TierTitles[TIER_IDS.ESSENTIAL].title}`
						);
					} else if (currentTierId === TIER_IDS.STANDARD && selectedTier === TIER_IDS.PREMIUM) {
						history.push(
							`${ROUTE_ACCOUNT_BEDROCK}?switchedTierFrom=${TierTitles[TIER_IDS.STANDARD].title}`
						);
					} else {
						history.push(ROUTE_ACCOUNT_BEDROCK);
					}
				} else {
					dispatch(showNotification(CHANGE_TIER_QUEUED_NOTIFICATION));
					history.push(
						`${ROUTE_ACCOUNT_BEDROCK}?tierChangeEffectiveDate=${switchTier.effectiveDate}`
					);
				}
			});
		}
	}, [
		selectedTier,
		tierCollection,
		gigyaUser.UID,
		currentTierId,
		subscriptionNumber,
		isUserOnFreeTier,
		showPromo,
		analyticsPush,
		changeSubscriptionTier,
		dispatch,
	]);

	const cancelChangeTier = useCallback(() => {
		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CHANGE,
			label: EVT_SELECT,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION_CHANGE,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: EVT_SUBSCRIPTION_CHANGE_PROCEED,
				elementLabel: BACK,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CHANGE,
				funnelStepName: 'Cancel New Subscription',
				funnelStep: '02',
				funnelSubscriptionType: selectedTier,
			},
			rtlUser: { rtlUserId: gigyaUser.UID },
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});

		analyticsPush({ label: CANCEL }, EVT_CHANGE_TIER_CANCEL);
		if (redirect) {
			window.history.back();
		} else {
			history.push(ROUTE_ACCOUNT_BEDROCK);
		}
	}, [selectedTier, gigyaUser.UID, subscriptionNumber, analyticsPush, redirect]);

	const handleConfirmClick = useCallback(() => {
		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CHANGE,
			label: EVT_SELECT,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION_CHANGE,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: EVT_SUBSCRIPTION_CHANGE_PROCEED,
				elementLabel: CHANGE_TIER_ACTION_BUTTON,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CHANGE,
				funnelStepName: 'Select New Subscription',
				funnelStep: '02',
				funnelSubscriptionType: selectedTier,
			},
			rtlUser: { rtlUserId: gigyaUser.UID },
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});

		setShowChangeTierModal(true);
	}, [selectedTier, gigyaUser.UID, subscriptionNumber, setShowChangeTierModal]);

	if (isTiersPending) {
		return (
			<section className="change-tier">
				<Spinner data-test="react-helper-spinner" />
			</section>
		);
	}

	if (isTiersError) {
		return (
			<section className="change-tier">
				<Error />
			</section>
		);
	}

	return (
		<section className="change-tier">
			{tierPresentation && (
				<ChangeTierModal
					active={showChangeTierModal}
					setActive={setShowChangeTierModal}
					tierPresentation={tierPresentation}
					gigyaUser={gigyaUser}
					currentTierId={currentTierId}
					selectedTier={selectedTier}
					changeTier={changeTier}
					confirmButtonDisabled={confirmButtonDisabled}
					subscriptionNumber
				/>
			)}

			<header className="change-tier__header">
				<DiscountedPriceNotification
					testId="change-tier-discounted-price-warning"
					className="change-tier__notification"
					copy={
						isUserOnPromoPeriod
							? NOTIFICATION_CHANGE_TIERS_PROMO
							: NOTIFICATION_CHANGE_TIERS_DISCOUNTED_PRICE
					}
					analyticsEventNameOnRender={EVT_CHANGE_TIER_NOTIFICATION_DISCOUNTED_PRICE}
				/>
				<h1 className="header2">{SUBSCRIPTION_TIER_B_TITLE}</h1>
				{showPromo && (
					<div
						className="change-tier__promo"
						/* eslint-disable-next-line react/no-danger*/
						dangerouslySetInnerHTML={{
							__html: getTrialPeriodText(ONBOARDING_TIER_OPTIONS_SUBTITLE_PROMO_BENEFIT_B),
						}}
					/>
				)}
				{blackFridayIsActive && BLACK_FRIDAY.TERMS_TEXT && (
					<p className="change-tier__promo-deal">
						<img
							className="tier-tip-container__button__icon"
							src={getImageUrl('/images/icons/info-black-friday-white.svg')}
							alt="info icon"
						/>
						<p
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: BLACK_FRIDAY.TERMS_TEXT,
							}}
						/>
					</p>
				)}
			</header>

			<div
				className={classnames('change-tier__tiers change-tier__tiersB', {
					['change-tier__tiers--free-tier change-tier__tiersB--free-tier']: isUserOnFreeTier,
				})}
			>
				{displayedTierCollection.map((tier) => {
					const discountedPrice = blackFridayIsActive
						? BLACK_FRIDAY[`TIER_PRICE_${tier.id}`]
						: undefined;
					const isBlackFriday =
						blackFridayIsActive &&
						(tier.id === TIER_IDS.ESSENTIAL || tier.id === TIER_IDS.STANDARD);
					return (
						<>
							{isBiggerTierCardsExperiment ? (
								<TierDescriptionB
									tierId={tier.id}
									pricing={formatPrice(tier.pricing.recurring)}
									discountedPrice={discountedPrice}
									key={tier.id}
									current={isCurrentTier(tier)}
									selected={isSelected(tier)}
									onClick={onTierChange}
									isBlackFriday={isBlackFriday}
								/>
							) : (
								<TierDescription
									tierId={tier.id}
									pricing={formatPrice(tier.pricing.recurring)}
									discountedPrice={discountedPrice}
									key={tier.id}
									current={isCurrentTier(tier)}
									selected={isSelected(tier)}
									onClick={onTierChange}
									isBlackFriday={isBlackFriday}
								/>
							)}
						</>
					);
				})}
			</div>
			{isBiggerTierCardsExperiment && (
				<div className="change-tier__tierControls">
					<button
						className="change-tier__tierControls-button change-tier__tierControls-button--left"
						onClick={() => handleCarouselNavigation('left')}
						disabled={navigationState.isFirstTier}
						aria-label="Selecteer vorige abonnement"
					>
						<Icon
							className="change-tier__tierControls-icon change-tier__tierControls-icon--left"
							name="arrow"
							width="24"
							height="24"
							viewBox="0 0 16 12"
							aria-hidden="true"
						/>
					</button>
					<h3 className="change-tier__tierControls-title">
						{tierPresentation?.title || ''}
						{selectedTier === currentTierId && ' \u2022 huidig'}
					</h3>
					<button
						className="change-tier__tierControls-button change-tier__tierControls-button--right"
						onClick={() => handleCarouselNavigation('right')}
						disabled={navigationState.isLastTier}
						aria-label="Selecteer volgende abonnement"
					>
						<Icon
							className="change-tier__tierControls-icon change-tier__tierControls-icon--right"
							name="arrow"
							width="24"
							height="24"
							viewBox="0 0 16 12"
							aria-hidden="true"
						/>
					</button>
				</div>
			)}
			<div className="change-tier__tier-table">
				<TierList tierFeatures={tierPresentation} />
			</div>

			<p className="change-tier__info">{SUBSCRIPTION_TIER_B_CHANGE_INFO}</p>
			{priceIncrease && (
				<div className="change-tier__price-increase">
					<p>{PRICE_INCREASE}</p>
					{/* eslint-disable-next-line react/no-danger*/}
					<p dangerouslySetInnerHTML={{ __html: PRICE_INCREASE_HELP_LINK }} />
				</div>
			)}
			<footer>
				<div className="change-tier__footer-actions">
					<Button
						large
						data-test="change-tier-cancel-button"
						kind={BUTTON_KIND_TRANSPARENT}
						onClick={cancelChangeTier}
					>
						{BACK}
					</Button>
					<Button
						large
						data-test="change-tier-confirm-button"
						kind={BUTTON_KIND_BRAND}
						onClick={handleConfirmClick}
						disabled={confirmButtonDisabled}
					>
						{CHANGE_TIER_ACTION_BUTTON}
					</Button>
				</div>
				<p className="change-tier__footer-footnote">{CHANGE_TIER_FOOTNOTE}</p>
			</footer>
		</section>
	);
};

export default ChangeTier;
